import { usePortalMessage } from "../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * Organization → Workspaces — `/organization/workspaces` (design sync 2026-10-09, Claude Design version
 * 1791495439483263). The design dropped its `Workspaces` screen (and the sidebar row); the list now lives on the
 * Organization page as its second tab, drawn with the Organization renderer's `list` block (the prototype's
 * `ORG_PAGES.workspaces`): "New workspace" in the block head, then one row per workspace — the storefront glyph
 * (an archive for an archived one), its name and a tag (Current / Active / Archiving / Archived), "3 people ·
 * 2 agents · shared balance", "3,118 min 20 s this month · €1,559.00", and its actions (Switch to, Open, Funding for a
 * balance of its own, Restore for an archived one). The old `/workspaces` path redirects here.
 *
 * Owners and Admins see every workspace of the organization; everyone else sees only the ones they belong to (the
 * API's listing decides — `readWorkspaceCards`). A person with no organization role that reads the organization
 * sees this tab alone.
 *
 * plan-015 WSK (G25, ADR 0050; owner answers 15, 33): a Test workspace's row draws the flask and the tag "Test", as the
 * prototype's "Sandbox" row does (`ORG_PAGES.workspaces`); the New workspace dialog asks "What it is for" (the
 * prototype's three choices — "A test or staging workspace" makes it Test). A label only: its figures, funding and
 * actions are any workspace's.
 *
 * Kept from P11-08 / plan-014 B3, which the design does not draw but which are real: "Who may create workspaces"
 * (FR12-WSP-01) as a block under the list, for the organization's people; the New workspace dialog (also opened by
 * the workspace picker's "New workspace", `?new=1`); paging ("Show more workspaces") when the organization has more
 * workspaces than one page. The design has no search box, so the list has none; the workspace's team is on its own
 * page (`/workspaces/$workspaceId`).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Button, Dialog, Field, Input, Segmented, Select } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import { GLYPH } from "../../shell/glyph.js";
import { useGateway } from "../../api/provider.js";
import { QUERY_KEY, authContextQuery } from "../../api/query.js";
import type {
  WorkspaceCardView,
  WorkspaceCreationPolicy,
  WorkspaceFundingKind,
  WorkspaceKind,
} from "../../api/slice/workspace-management.js";
import { useMemberCan } from "../../shell/member-capability.js";
import { useSwitchWorkspace } from "../../shell/workspace-switch.js";
import { apiProblemText } from "../../api/api-error.js";
import "./workspaces.css";
import { formatMoney } from "../../money.js";
import { talkTimeText } from "../../talk-time.js";
import { Block, BlockNote, ItemColumn, ItemRow, OrganizationPage } from "../account-admin/page/organization-page.js";
import { browserZone, zoneChoice } from "./offered-zone.js";
import { WORKSPACE_NAME_MAXIMUM, nameLengthOf } from "./workspace-name.js";

/** The query key of the cards (under `QUERY_KEY.workspace`, so every workspace change refreshes them). */
export const workspaceCardKey = (q: string): readonly unknown[] => [...QUERY_KEY.workspace, "cards", q];

/** The tab's search parameters: `new` opens the New workspace dialog on arrival (the workspace picker's link). */
export interface WorkspacesSearch {
  readonly new?: true;
}

/** The route's `validateSearch`: a total function over the address bar. */
export function workspacesSearchOf(raw: Readonly<Record<string, unknown>>): WorkspacesSearch {
  return raw["new"] === true || raw["new"] === "true" || raw["new"] === 1 || raw["new"] === "1" ? { new: true } : {};
}

/**
 * "3 people · 2 agents · shared balance" — a row's second line. A balance of its own reads "own balance €120.00" when
 * the API served its amount (plan-015 BAL, G24: only to who may read that balance), and "own balance" otherwise.
 */
export function workspaceMetaOf(one: Pick<WorkspaceCardView, "memberCount" | "voiceAgentCount" | "funding" | "dedicatedBalance">): string {
  const people = portalText("core", "workspaces.people_count", { count: one.memberCount });
  const voiceAgentText = one.voiceAgentCount === 0 ? portalText("core", "workspaces.no_agents_e6a02e") : portalText("core", "workspaces.agents_count", { count: one.voiceAgentCount });
  const funding =
    one.funding.kind === "dedicated" ? (one.dedicatedBalance === null ? portalText("core", "workspaces.own_balance_e75238") : portalText("core", "workspaces.own_balance_b316b0", { value0: formatMoney(one.dedicatedBalance) })) : portalText("core", "workspaces.shared_balance_0aef8f");
  return portalText("core", "workspaces._def449", { value0: people, value1: voiceAgentText, value2: funding });
}

/** "3,118 min 20 s this month · €1,559.00" — a row's figures on the right; "—" for an archived workspace. */
export function workspaceUsageOf(one: Pick<WorkspaceCardView, "state" | "monthTalkSecond" | "monthSpend">): string {
  if (one.state === "archived") return "—";
  return portalText("core", "workspaces._this_month_69db0f", { value0: talkTimeText(one.monthTalkSecond), value1: formatMoney(one.monthSpend) });
}

const TAG_OF: Readonly<Record<WorkspaceCardView["state"], string>> = { get active() { return portalText("core", "workspaces.active_a733b8"); }, get draining() { return portalText("core", "workspaces.archiving_7f96b2"); }, get archived() { return portalText("core", "account_admin.archived_eddc81"); } };

/**
 * A row's tag: "Current" for the workspace open now, then the lifecycle — except that an active Test workspace reads
 * "Test" (the prototype's "Sandbox" row: flask, "Test"). Archiving and Archived say what is happening, whatever the kind.
 */
export function workspaceTagOf(one: Pick<WorkspaceCardView, "state" | "kind">, current: boolean): string {
  if (current) return portalText("core", "dashboard.current_4fc0e2");
  if (one.state === "active" && one.kind === "test") return portalText("core", "workspaces.test_640ab2");
  return TAG_OF[one.state];
}

/** A row's glyph: the archive for an archived workspace, the flask for a Test one, else the storefront. */
export function workspaceGlyphOf(one: Pick<WorkspaceCardView, "state" | "kind">): (typeof GLYPH)[keyof typeof GLYPH] {
  if (one.state === "archived") return GLYPH.archive;
  return one.kind === "test" ? GLYPH.flask : GLYPH.storefront;
}

/** The New workspace dialog's "What it is for" — the prototype's three choices; only the third is a kind of its own. */
export type WorkspacePurpose = "second_location" | "different_business" | "test_or_staging";

export const WORKSPACE_PURPOSE: readonly { readonly value: WorkspacePurpose; readonly label: string }[] = [
  { value: "second_location", get label() { return portalText("core", "workspaces.a_second_location_553273"); } },
  { value: "different_business", get label() { return portalText("core", "workspaces.a_different_business_7c69a5"); } },
  { value: "test_or_staging", get label() { return portalText("core", "workspaces.a_test_or_staging_workspace_c74bca"); } },
];

/** The kind a purpose stores: a test or staging workspace is `test`; a second location or business is `standard`. */
export function workspaceKindOfPurpose(purpose: WorkspacePurpose): WorkspaceKind {
  return purpose === "test_or_staging" ? "test" : "standard";
}

export interface WorkspacesScreenProps {
  readonly search: WorkspacesSearch;
  readonly onSearch: (next: WorkspacesSearch) => void;
}

export function WorkspacesScreen({ search, onSearch }: WorkspacesScreenProps): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const context = useQuery(authContextQuery(gateway));
  const [extra, setExtra] = useState<{ readonly cursor: string; readonly row: readonly WorkspaceCardView[] } | null>(null);

  const member = useMemberCan();
  const scoped = context.data === undefined || context.data === null || context.data.kind === "selection_required" ? null : context.data;
  const accountId = scoped?.accountId ?? null;
  const currentId = scoped?.kind === "workspace_selected" ? scoped.workspaceId : null;
  const readsFinance = scoped?.capability.account.includes("account.finance.read") === true;
  const managesWorkspaces = scoped?.capability.account.includes("account.workspace.manage") === true;
  const workspaceSwitch = useSwitchWorkspace(accountId);
  const cards = useQuery({
    queryKey: workspaceCardKey(""),
    queryFn: () => gateway.readWorkspaceCards({ q: null, cursor: null }),
  });

  const more = useMutation({
    mutationFn: (cursor: string) => gateway.readWorkspaceCards({ q: null, cursor }),
    onSuccess: (page) => {
      setExtra((held) => ({ cursor: page.nextCursor ?? "", row: [...(held?.row ?? []), ...page.workspace] }));
    },
  });

  const row = [...(cards.data?.workspace ?? []), ...(extra?.row ?? [])];
  const nextCursor = extra === null ? (cards.data?.nextCursor ?? null) : extra.cursor === "" ? null : extra.cursor;
  const lede =
    portalText("core", "workspaces.each_workspace_has_its_own_agents_numbers_and_people_owners_and_a_2a7084");
  const createRefusal = member.whyNot("workspace.create");
  const newButton = (
    <Button
      variant="primary"
      disabled={!member.can("workspace.create")}
      title={createRefusal ?? undefined}
      onClick={() => {
        onSearch({ new: true });
      }}
    >{portalText("core", "workspaces.new_workspace_b21b86")}</Button>
  );

  return (
    <OrganizationPage tab="workspaces" title={portalText("core", "account_admin.workspaces_205b45")} lede={lede}>
      {workspaceSwitch.problem === null ? null : (
        <p className="c-note c-note-critical op-note" role="alert">{portalText("core", "workspaces.that_workspace_could_not_be_opened_5b8d80")}{" "}{workspaceSwitch.problem}
        </p>
      )}
      <Block buttons={newButton} label={portalText("core", "account_admin.workspaces_205b45")}>
        {cards.data === undefined ? (
          cards.isError ? (
            <p className="c-note c-note-critical op-note" role="alert">{portalText("core", "workspaces.your_workspaces_could_not_be_read_a10ec2")}{" "}{apiProblemText(cards.error, "")}{" "}
              <Button
                variant="ghost"
                onClick={() => {
                  void cards.refetch();
                }}
              >{portalText("core", "workspaces.try_again_042c86")}</Button>
            </p>
          ) : (
            <BlockNote>{portalText("core", "workspaces.reading_your_workspaces_932dae")}</BlockNote>
          )
        ) : row.length === 0 ? (
          <BlockNote>{portalText("core", "workspaces.no_workspaces_yet_70e928")}</BlockNote>
        ) : (
          <ItemColumn label={portalText("core", "account_admin.workspaces_205b45")}>
            {row.map((one) => {

              const current = one.workspaceId === currentId;
              const archived = one.state === "archived";
              return (
                <ItemRow
                  key={one.workspaceId}
                  glyph={workspaceGlyphOf(one)}
                  tone={current ? "accent" : "neutral"}
                  title={one.name}
                  tag={workspaceTagOf(one, current)}
                  dek={archived ? portalText("core", "workspaces.archived_history_and_invoices_kept_fde679") : workspaceMetaOf(one)}
                  meta={workspaceUsageOf(one)}
                  actions={
                    archived ? (
                      managesWorkspaces ? (
                        <Link to="/organization/archive-close" className="btn btn-secondary">{portalText("core", "account_admin.restore_3cbe6d")}</Link>
                      ) : undefined
                    ) : (
                      <>
                        {current || one.memberRole === null || one.state !== "active" ? null : (
                          <Button
                            variant="secondary"
                            disabled={workspaceSwitch.pending}
                            aria-label={portalText("core", "workspaces.switch_to_ed22ac", { value0: one.name })}
                            onClick={() => {
                              workspaceSwitch.switchTo(one.workspaceId);
                            }}
                          >{portalText("core", "workspaces.switch_to_87de32")}</Button>
                        )}
                        <Link
                          to="/workspaces/$workspaceId"
                          params={{ workspaceId: one.workspaceId }}
                          className="btn btn-secondary"
                          aria-label={portalText("core", "workspaces.open_4c1e7f", { value0: one.name })}
                        >{portalText("core", "dashboard.open_cf9b77")}</Link>
                        {one.funding.kind === "dedicated" && readsFinance ? (
                          <Link to="/finance/funding" className="btn btn-secondary" aria-label={portalText("core", "workspaces.funding_of_75cadd", { value0: one.name })}>{portalText("core", "workspaces.funding_6ff171")}</Link>
                        ) : null}
                      </>
                    )
                  }
                />
              );
            })}
          </ItemColumn>
        )}
        {nextCursor === null ? null : (
          <span className="c-ws-more">
            <Button
              variant="secondary"
              disabled={more.isPending}
              onClick={() => {
                more.mutate(nextCursor);
              }}
            >{portalText("core", "workspaces.show_more_workspaces_126715")}</Button>
          </span>
        )}
      </Block>

      {scoped === null || scoped.accountRole === null ? null : <CreationPolicyBlock accountId={scoped.accountId} />}

      {accountId === null ? null : (
        <NewWorkspaceDialog
          open={search.new === true && member.can("workspace.create")}
          accountId={accountId}
          maySpend={member.can("billing.write")}
          onClose={() => {
            onSearch({});
          }}
        />
      )}
    </OrganizationPage>
  );
}

const POLICY_WORD: Readonly<Record<WorkspaceCreationPolicy, string>> = {
  get owner_and_admin() { return portalText("core", "workspaces.owner_and_admins_28a29a"); },
  get owner_only() { return portalText("core", "workspaces.owner_only_e91f02"); },
};

/** "Who may create workspaces" (FR12-WSP-01), for the organization's people; changed by whoever the API allows. */
function CreationPolicyBlock({ accountId }: { readonly accountId: string }): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const key = [...QUERY_KEY.workspace, "creation-policy", accountId];
  const policy = useQuery({ queryKey: key, queryFn: () => gateway.readWorkspaceCreationPolicy(accountId) });
  const [problem, setProblem] = usePortalMessage();
  const [saved, setSaved] = usePortalMessage();
  const change = useMutation({
    mutationFn: (next: WorkspaceCreationPolicy) => {
      if (policy.data === undefined) throw new Error(portalText("core", "workspaces.the_policy_has_not_been_read_yet_95390a"));
      return gateway.changeWorkspaceCreationPolicy(accountId, next, policy.data.revision);
    },
    onSuccess: async (changed) => {
      setProblem(null);
      setSaved(() => (changed.policy === "owner_only" ? portalText("core", "workspaces.saved_only_the_owner_may_create_workspaces_ce5c54") : portalText("core", "workspaces.saved_the_owner_and_admins_may_create_workspaces_e10142")));
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.workspace });
    },
    onError: (error: unknown) => {
      setSaved(null);
      setProblem(() => (apiProblemText(error, portalText("core", "workspaces.that_could_not_be_saved_2daac8"))));
    },
  });
  // Someone with no organization role here reads nothing: the block is simply absent.
  if (policy.data === undefined) return null;
  return (
    <Block title={portalText("core", "workspaces.who_may_create_workspaces_595005")}>
      <div className="c-ws-policy">
      {policy.data.canChange ? (
        <Segmented<WorkspaceCreationPolicy>
          name="workspace-creation-policy"
          legend={portalText("core", "workspaces.who_may_create_workspaces_595005")}
          options={[
            { value: "owner_and_admin", label: POLICY_WORD.owner_and_admin },
            { value: "owner_only", label: POLICY_WORD.owner_only },
          ]}
          value={policy.data.policy}
          onChange={(next) => {
            if (next !== policy.data?.policy) change.mutate(next);
          }}
        />
      ) : (
        <p className="c-note">{POLICY_WORD[policy.data.policy]}.</p>
      )}
      <p className="c-ws-policy-dek">
        {policy.data.policy === "owner_only"
          ? portalText("core", "workspaces.admins_can_still_manage_the_workspaces_they_are_in_59babb")
          : portalText("core", "workspaces.the_default_the_owner_and_the_organization_s_admins_create_worksp_14f41a")}
      </p>
      {problem === null ? null : (
        <p className="c-note c-note-critical" role="alert">
          {problem}
        </p>
      )}
      {saved === null ? null : (
        <p className="c-note" role="status">
          {saved}
        </p>
      )}
      </div>
    </Block>
  );
}

/** The New workspace dialog — also opened from the Organization overview's "New workspace" (plan-014 SC-P). */
export function NewWorkspaceDialog({
  open,
  accountId,
  maySpend,
  onClose,
}: {
  readonly open: boolean;
  readonly accountId: string;
  /** Whether the person may choose a balance of its own (`account.finance.manage`). */
  readonly maySpend: boolean;
  readonly onClose: () => void;
}): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  // The browser's zone first, because it is a measurement; nothing is chosen when it cannot say.
  const [timeZone, setTimeZone] = useState(browserZone() ?? "");
  const [funding, setFunding] = useState<WorkspaceFundingKind>("shared");
  const [purpose, setPurpose] = useState<WorkspacePurpose>("second_location");
  const [slug, setSlug] = useState("");
  const [message, setMessage] = usePortalMessage();

  const create = useMutation({
    mutationFn: () =>
      gateway.createWorkspaceInAccount(accountId, {
        name: name.trim(),
        slug: slug.trim() === "" ? null : slug.trim().toLowerCase(),
        timeZone,
        funding: maySpend ? funding : "shared",
        kind: workspaceKindOfPurpose(purpose),
      }),
    onSuccess: async () => {
      setName("");
      setSlug("");
      setFunding("shared");
      setPurpose("second_location");
      setMessage(null);
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.workspace });
      onClose();
    },
    onError: (error: unknown) => {
      setMessage(() => (apiProblemText(error, portalText("core", "workspaces.that_workspace_could_not_be_created_fcc4e2"))));
    },
  });

  const zone = zoneChoice(timeZone === "" ? null : timeZone);
  const nameLength = nameLengthOf(name);
  const nameTooLong = nameLength > WORKSPACE_NAME_MAXIMUM;

  return (
    <Dialog
      open={open}
      title={portalText("core", "workspaces.create_a_workspace_e94c64")}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>{portalText("core", "workspaces.cancel_77dfd2")}</Button>
          <Button
            variant="primary"
            disabled={nameLength === 0 || nameTooLong || timeZone === "" || create.isPending}
            onClick={() => {
              create.mutate();
            }}
          >{portalText("core", "workspaces.create_workspace_8e0938")}</Button>
        </>
      }
    >
      <p className="c-note">{portalText("core", "workspaces.a_new_workspace_starts_empty_with_its_own_agents_numbers_and_peop_1a2d7d")}</p>
      <Field
        label={portalText("core", "workspaces.workspace_name_6e0f2b")}
        description={portalText("core", "workspaces.the_name_of_your_business_or_location_as_your_team_will_see_it_of_2eef9a", { value0: String(nameLength), value1: String(WORKSPACE_NAME_MAXIMUM) })}
        {...(nameTooLong ? { error: portalText("core", "workspaces.a_workspace_name_can_be_at_most_characters_a6bb31", { value0: String(WORKSPACE_NAME_MAXIMUM) }) } : {})}
      >
        {(control) => (
          <Input
            {...control}
            value={name}
            onChange={(event) => {
              setName(event.currentTarget.value);
            }}
          />
        )}
      </Field>
      <Field
        label={portalText("core", "workspaces.what_it_is_for_9d49f2")}
        description={
          purpose === "test_or_staging"
            ? portalText("core", "workspaces.marked_test_on_the_list_it_is_billed_like_any_other_workspace_and_067281")
            : portalText("core", "workspaces.only_a_test_or_staging_workspace_is_marked_on_the_list_4d061d")
        }
      >
        {(control) => (
          <Select
            {...control}
            value={purpose}
            onChange={(event) => {
              const next = WORKSPACE_PURPOSE.find((one) => one.value === event.currentTarget.value);
              if (next !== undefined) setPurpose(next.value);
            }}
          >
            {WORKSPACE_PURPOSE.map((one) => (
              <option key={one.value} value={one.value}>
                {one.label}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field
        label={portalText("core", "workspaces.paid_by_70c115")}
        description={
          maySpend
            ? portalText("core", "workspaces.only_owners_admins_and_finance_can_choose_a_balance_of_its_own_st_be77ee")
            : portalText("core", "workspaces.the_organization_s_shared_balance_only_owners_admins_and_finance_8f406b")
        }
      >
        {() =>
          maySpend ? (
            <Segmented<WorkspaceFundingKind>
              name="workspace-funding"
              legend={portalText("core", "workspaces.paid_by_70c115")}
              options={[
                { value: "shared", label: portalText("core", "workspaces.shared_balance_dad39d") },
                { value: "dedicated", label: portalText("core", "workspaces.a_balance_of_its_own_6d1533") },
              ]}
              value={funding}
              onChange={setFunding}
            />
          ) : (
            <span className="c-ws-funding-fixed">{portalText("core", "workspaces.shared_balance_dad39d")}</span>
          )
        }
      </Field>
      <Field label={portalText("core", "auth.time_zone_eea79a")} description={portalText("core", "workspaces.opening_hours_and_the_billing_day_are_both_taken_in_this_zone_429454")}>
        {(control) => (
          <Select
            {...control}
            value={timeZone}
            onChange={(event) => {
              setTimeZone(event.currentTarget.value);
            }}
          >
            {timeZone === "" ? <option value="">{portalText("core", "workspaces.choose_a_time_zone_aaebf5")}</option> : null}
            {zone.map((one) => (
              <option key={one} value={one}>
                {one}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field
        label={portalText("core", "workspaces.web_address_optional_0e2250")}
        description={portalText("core", "workspaces.lower_case_letters_digits_and_dashes_made_from_the_name_when_left_15fa4f")}
      >
        {(control) => (
          <Input
            {...control}
            value={slug}
            placeholder={portalText("core", "workspaces.roma_ostia_b6a274")}
            onChange={(event) => {
              setSlug(event.currentTarget.value);
            }}
          />
        )}
      </Field>
      <p className="c-note">{portalText("core", "workspaces.invite_people_from_the_workspace_s_page_once_it_exists_0b5083")}</p>
      {message === null ? null : <p className="c-note c-note-critical">{message}</p>}
    </Dialog>
  );
}
